import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import BottomQuickActions from "../components/BottomQuickActions";
import { useLiveFilter, getFollowUpSearchText } from "../hooks/useLiveFilter";
import { useSearchFilter } from "../layouts/AppLayout";
import {
  CalendarCheck,
  Clock,
  Warning,
  CheckCircle,
  User,
  Briefcase,
  FileText,
  Receipt,
  Plus,
  CaretDown,
  Eye,
  EyeSlash,
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const formatDueDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  if (date.getTime() === today.getTime()) return "Today";
  if (date.getTime() === tomorrow.getTime()) return "Tomorrow";
  
  const diffDays = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
  if (diffDays <= 7) return date.toLocaleDateString([], { weekday: 'long' });
  
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const getLinkedEntityIcon = (type) => {
  switch (type) {
    case "job": return Briefcase;
    case "estimate": return FileText;
    case "invoice": return Receipt;
    default: return null;
  }
};

function ReminderCard({ reminder, onComplete, onReopen }) {
  const [completing, setCompleting] = useState(false);
  const isOverdue = reminder.due_date < new Date().toISOString().split('T')[0] && reminder.status === "open";
  const isToday = reminder.due_date === new Date().toISOString().split('T')[0];
  const EntityIcon = getLinkedEntityIcon(reminder.linked_entity_type);
  
  const handleToggle = async () => {
    setCompleting(true);
    try {
      if (reminder.status === "completed") {
        await onReopen(reminder.reminder_id);
      } else {
        await onComplete(reminder.reminder_id);
      }
    } finally {
      setCompleting(false);
    }
  };
  
  return (
    <div 
      className={`p-4 border-2 rounded-lg transition-all ${
        reminder.status === "completed" 
          ? "border-zinc-200 bg-zinc-50 opacity-60" 
          : isOverdue 
            ? "border-red-200 bg-red-50" 
            : isToday 
              ? "border-amber-200 bg-amber-50" 
              : "border-zinc-200 bg-white"
      }`}
      data-testid={`reminder-${reminder.reminder_id}`}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <div className="pt-0.5">
          <Checkbox
            checked={reminder.status === "completed"}
            onCheckedChange={handleToggle}
            disabled={completing}
            className="border-2"
            data-testid={`reminder-checkbox-${reminder.reminder_id}`}
          />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Customer name */}
          <Link 
            to={`/customers/${reminder.customer_id}`}
            className="font-medium text-zinc-900 hover:text-[#FF6600] transition-colors"
            data-testid={`reminder-customer-link-${reminder.reminder_id}`}
          >
            <User size={14} className="inline mr-1 text-zinc-400" />
            {reminder.customer_name}
          </Link>
          
          {/* Linked entity */}
          {reminder.linked_entity_type && EntityIcon && (
            <div className="text-xs text-zinc-500 mt-1">
              <EntityIcon size={12} className="inline mr-1" />
              {reminder.linked_entity_title}
            </div>
          )}
          
          {/* Note */}
          <p className={`text-sm mt-2 ${reminder.status === "completed" ? "line-through text-zinc-400" : "text-zinc-700"}`}>
            {reminder.note}
          </p>
          
          {/* Due date badge */}
          <div className="flex items-center gap-2 mt-2">
            <Badge 
              variant="secondary" 
              className={`text-xs ${
                reminder.status === "completed"
                  ? "bg-green-100 text-green-700"
                  : isOverdue 
                    ? "bg-red-100 text-red-700" 
                    : isToday 
                      ? "bg-amber-100 text-amber-700" 
                      : "bg-zinc-100 text-zinc-600"
              }`}
            >
              {reminder.status === "completed" ? (
                <><CheckCircle size={12} className="mr-1" /> Completed</>
              ) : isOverdue ? (
                <><Warning size={12} className="mr-1" /> {formatDueDate(reminder.due_date)}</>
              ) : (
                <><Clock size={12} className="mr-1" /> {formatDueDate(reminder.due_date)}</>
              )}
            </Badge>
            
            {reminder.assigned_user_name && (
              <span className="text-xs text-zinc-400">
                Assigned to {reminder.assigned_user_name}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FollowUps() {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ overdue: 0, today: 0, upcoming: 0, completed: 0 });
  const [showCompleted, setShowCompleted] = useState(false);
  const [filter, setFilter] = useState("all"); // "all", "overdue", "today", "upcoming"

  // Use shared filter from layout header
  const { followUpsFilter: filterQuery } = useSearchFilter();

  const fetchReminders = useCallback(async () => {
    try {
      setLoading(true);
      const status = showCompleted ? "all" : "open";
      const response = await fetch(`${API}/reminders?status=${status}&limit=100`, {
        credentials: "include"
      });
      if (response.ok) {
        const data = await response.json();
        setReminders(data.reminders || []);
        setCounts(data.counts || {});
      }
    } catch (error) {
      console.error("Error fetching reminders:", error);
      toast.error("Failed to load follow-ups");
    } finally {
      setLoading(false);
    }
  }, [showCompleted]);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  const handleComplete = async (reminderId) => {
    try {
      const response = await fetch(`${API}/reminders/${reminderId}/complete`, {
        method: "PATCH",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("Follow-up completed");
        fetchReminders();
      } else {
        toast.error("Failed to complete follow-up");
      }
    } catch (error) {
      toast.error("Failed to complete follow-up");
    }
  };

  const handleReopen = async (reminderId) => {
    try {
      const response = await fetch(`${API}/reminders/${reminderId}/reopen`, {
        method: "PATCH",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("Follow-up reopened");
        fetchReminders();
      } else {
        toast.error("Failed to reopen follow-up");
      }
    } catch (error) {
      toast.error("Failed to reopen follow-up");
    }
  };

  // Memoized search text extractor for follow-ups
  const followUpSearchText = useCallback(getFollowUpSearchText, []);

  // Filter reminders based on status selection first
  const today = new Date().toISOString().split('T')[0];
  const statusFilteredReminders = useMemo(() => {
    return reminders.filter(r => {
      if (filter === "overdue") return r.due_date < today && r.status === "open";
      if (filter === "today") return r.due_date === today && r.status === "open";
      if (filter === "upcoming") return r.due_date > today && r.status === "open";
      if (filter === "completed") return r.status === "completed";
      return true;
    });
  }, [reminders, filter, today]);

  // Apply text filter using useLiveFilter hook
  const filteredReminders = useLiveFilter(statusFilteredReminders, filterQuery, followUpSearchText);

  // Group by category for "all" view - apply text filter to each group
  const textFilteredReminders = useLiveFilter(reminders, filterQuery, followUpSearchText);
  const overdueReminders = textFilteredReminders.filter(r => r.due_date < today && r.status === "open");
  const todayReminders = textFilteredReminders.filter(r => r.due_date === today && r.status === "open");
  const upcomingReminders = textFilteredReminders.filter(r => r.due_date > today && r.status === "open");
  const completedReminders = textFilteredReminders.filter(r => r.status === "completed");

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <button
          onClick={() => setFilter(filter === "overdue" ? "all" : "overdue")}
          className={`p-4 rounded-lg border-2 text-left transition-all ${
            filter === "overdue" ? "border-red-500 bg-red-50" : "border-zinc-200 hover:border-red-300"
          }`}
          data-testid="filter-overdue"
        >
          <Warning size={24} className="text-red-500 mb-2" />
          <div className="text-2xl font-bold text-red-600">{counts.overdue}</div>
          <div className="text-xs text-zinc-500 uppercase">Overdue</div>
        </button>
        
        <button
          onClick={() => setFilter(filter === "today" ? "all" : "today")}
          className={`p-4 rounded-lg border-2 text-left transition-all ${
            filter === "today" ? "border-amber-500 bg-amber-50" : "border-zinc-200 hover:border-amber-300"
          }`}
          data-testid="filter-today"
        >
          <Clock size={24} className="text-amber-500 mb-2" />
          <div className="text-2xl font-bold text-amber-600">{counts.today}</div>
          <div className="text-xs text-zinc-500 uppercase">Due Today</div>
        </button>
        
        <button
          onClick={() => setFilter(filter === "upcoming" ? "all" : "upcoming")}
          className={`p-4 rounded-lg border-2 text-left transition-all ${
            filter === "upcoming" ? "border-blue-500 bg-blue-50" : "border-zinc-200 hover:border-blue-300"
          }`}
          data-testid="filter-upcoming"
        >
          <CalendarCheck size={24} className="text-blue-500 mb-2" />
          <div className="text-2xl font-bold text-blue-600">{counts.upcoming}</div>
          <div className="text-xs text-zinc-500 uppercase">Upcoming</div>
        </button>
        
        <button
          onClick={() => { setShowCompleted(true); setFilter("completed"); }}
          className={`p-4 rounded-lg border-2 text-left transition-all ${
            filter === "completed" ? "border-green-500 bg-green-50" : "border-zinc-200 hover:border-green-300"
          }`}
          data-testid="filter-completed"
        >
          <CheckCircle size={24} className="text-green-500 mb-2" />
          <div className="text-2xl font-bold text-green-600">{counts.completed}</div>
          <div className="text-xs text-zinc-500 uppercase">Completed</div>
        </button>
      </div>

      {/* Show/Hide Completed Toggle & Filter Indicator */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {filter !== "all" && (
            <Button variant="ghost" size="sm" onClick={() => setFilter("all")} data-testid="show-all-btn">
              ← Show All
            </Button>
          )}
          {filterQuery && (
            <span className="text-sm text-zinc-500" data-testid="filter-count">
              Showing {textFilteredReminders.length} of {reminders.length} follow-ups
            </span>
          )}
        </div>
        <button
          onClick={() => { setShowCompleted(!showCompleted); if (filter === "completed") setFilter("all"); }}
          className="flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-700"
          data-testid="toggle-completed"
        >
          {showCompleted ? <EyeSlash size={16} /> : <Eye size={16} />}
          {showCompleted ? "Hide Completed" : "Show Completed"}
        </button>
      </div>

      {/* Reminders List */}
      {loading ? (
        <Card className="border-2 border-zinc-200">
          <CardContent className="p-8 text-center">
            <div className="animate-pulse">
              <div className="h-4 bg-zinc-200 rounded w-1/3 mx-auto mb-2"></div>
              <div className="h-4 bg-zinc-200 rounded w-1/2 mx-auto"></div>
            </div>
          </CardContent>
        </Card>
      ) : textFilteredReminders.length === 0 && filterQuery ? (
        <Card className="border-2 border-dashed border-zinc-300">
          <CardContent className="p-8 text-center text-zinc-400">
            <CalendarCheck size={32} className="mx-auto mb-2 text-zinc-300" />
            <p>No follow-ups match "{filterQuery}"</p>
            <p className="text-xs mt-1">Try a different search term</p>
          </CardContent>
        </Card>
      ) : filteredReminders.length === 0 ? (
        <Card className="border-2 border-dashed border-zinc-300">
          <CardContent className="p-8 text-center text-zinc-400">
            <CalendarCheck size={32} className="mx-auto mb-2 text-zinc-300" />
            <p>{filter === "all" ? "No follow-ups yet" : `No ${filter} follow-ups`}</p>
            <p className="text-xs mt-1">Add follow-ups from customer pages</p>
          </CardContent>
        </Card>
      ) : filter === "all" ? (
        // Grouped view
        <div className="space-y-6">
          {/* Overdue */}
          {overdueReminders.length > 0 && (
            <div>
              <h2 className="font-heading text-sm font-semibold uppercase tracking-tight text-red-600 mb-3 flex items-center gap-2">
                <Warning size={16} /> Overdue ({overdueReminders.length})
              </h2>
              <div className="space-y-3">
                {overdueReminders.map(r => (
                  <ReminderCard key={r.reminder_id} reminder={r} onComplete={handleComplete} onReopen={handleReopen} />
                ))}
              </div>
            </div>
          )}
          
          {/* Today */}
          {todayReminders.length > 0 && (
            <div>
              <h2 className="font-heading text-sm font-semibold uppercase tracking-tight text-amber-600 mb-3 flex items-center gap-2">
                <Clock size={16} /> Due Today ({todayReminders.length})
              </h2>
              <div className="space-y-3">
                {todayReminders.map(r => (
                  <ReminderCard key={r.reminder_id} reminder={r} onComplete={handleComplete} onReopen={handleReopen} />
                ))}
              </div>
            </div>
          )}
          
          {/* Upcoming */}
          {upcomingReminders.length > 0 && (
            <div>
              <h2 className="font-heading text-sm font-semibold uppercase tracking-tight text-blue-600 mb-3 flex items-center gap-2">
                <CalendarCheck size={16} /> Upcoming ({upcomingReminders.length})
              </h2>
              <div className="space-y-3">
                {upcomingReminders.map(r => (
                  <ReminderCard key={r.reminder_id} reminder={r} onComplete={handleComplete} onReopen={handleReopen} />
                ))}
              </div>
            </div>
          )}
          
          {/* Completed */}
          {showCompleted && completedReminders.length > 0 && (
            <div>
              <h2 className="font-heading text-sm font-semibold uppercase tracking-tight text-green-600 mb-3 flex items-center gap-2">
                <CheckCircle size={16} /> Completed ({completedReminders.length})
              </h2>
              <div className="space-y-3">
                {completedReminders.map(r => (
                  <ReminderCard key={r.reminder_id} reminder={r} onComplete={handleComplete} onReopen={handleReopen} />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        // Filtered view
        <div className="space-y-3">
          {filteredReminders.map(r => (
            <ReminderCard key={r.reminder_id} reminder={r} onComplete={handleComplete} onReopen={handleReopen} />
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <BottomQuickActions />
    </div>
  );
}
